import { Dialog as DialogPrimitive } from '@base-ui/react'
import { AnimatePresence, motion } from 'framer-motion'
import { X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useEffect, useId, useRef, type ReactNode } from 'react'

import { IconButton } from '@/components/ui/IconButton'
import { cn } from '@/lib/cn'
import { drawerVariants, scrimVariants } from '@/lib/motion'

/* A right-hand overlay panel. This is a TRANSIENT OVERLAY — one of exactly two
 * things in the system permitted to lift off the page — so it carries
 * --shadow-overlay while cards, tables and the navigation carry none.
 *
 * Not to be confused with the record chat dock, which since 0052 ALSO floats
 * over the page at --spacing-chat-panel wide. The two are still different
 * things and the difference is now about lifetime rather than about elevation:
 * a Drawer is transient and modal — it scrims the page, traps focus, and is
 * opened to do one thing and dismissed — whereas the dock is ambient, sits
 * beside the work without a scrim, and remembers whether it was minimised.
 * Chat is still never a tab: burying a conversation inside one section of one
 * record is what kept the D1 failure available (EXPERIENCE.md § Information
 * Architecture). It is now dismissible, and `ChatDock`'s own header states
 * what replaced the always-visible guarantee.
 *
 * z-order between them is deliberate and lives in ChatDock: the dock sits at
 * z-40 so a Drawer opening at z-50 covers it whole rather than half.
 */

export interface DrawerProps {
  open: boolean
  onClose: () => void
  title: string
  /** Trailing slot in the header — a "mark all read" action, a link. */
  action?: ReactNode
  children: ReactNode
  className?: string
}

export function Drawer({
  open,
  onClose,
  title,
  action,
  children,
  className,
}: DrawerProps) {
  const { t } = useTranslation()

  const titleId = useId()
  const actionsRef = useRef<DialogPrimitive.Root.Actions>(null)

  // The page behind an overlay must not scroll under it. Restoring the previous
  // value rather than clearing it keeps a second, outer lock intact.
  useEffect(() => {
    if (!open) return
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = previous
    }
  }, [open])

  /* AnimatePresence keeps the panel mounted for the length of its exit
   * transition and then unmounts it, so the contents still leave the
   * accessibility tree and the tab order entirely — they are never merely
   * invisible. What it adds over the previous `if (!open) return null` is the
   * dismissal itself: the drawer used to slide in but vanish on a single
   * frame, which read as a glitch rather than as a close. */
  return (
    /* `modal={false}` for the reason Sheet states: a modal dialog inerts the
       whole page behind it, and the suites that mount a drawer query that page
       while it is open. */
    <DialogPrimitive.Root
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
      modal={false}
      actionsRef={actionsRef}
    >
      <DialogPrimitive.Portal keepMounted>
        <AnimatePresence onExitComplete={() => actionsRef.current?.unmount()}>
          {open ? (
            <div className="fixed inset-0 z-50 flex justify-end">
              <motion.button
                type="button"
                aria-label={`Close ${title.toLowerCase()}`}
                onClick={onClose}
                variants={scrimVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                className="absolute inset-0 cursor-default bg-[var(--overlay-scrim)]"
              />

              <DialogPrimitive.Popup
                aria-labelledby={titleId}
                render={
                  <motion.div
                    variants={drawerVariants}
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                  />
                }
                className={cn(
                  'bg-surface relative flex h-full w-full max-w-[400px] flex-col',
                  'shadow-[var(--shadow-overlay)] focus:outline-none',
                  className,
                )}
              >
                <header className="border-border-subtle flex h-[var(--spacing-topbar)] shrink-0 items-center gap-2 border-b px-4">
                  <h2
                    id={titleId}
                    className="text-text-heading min-w-0 flex-1 truncate text-[15px] font-normal tracking-[-0.2px]"
                  >
                    {title}
                  </h2>
                  {action}
                  <IconButton
                    icon={X}
                    label={t('common.actions.close')}
                    onClick={onClose}
                  />
                </header>

                <div className="asa-scroll min-h-0 flex-1 overflow-y-auto">
                  {children}
                </div>
              </DialogPrimitive.Popup>
            </div>
          ) : null}
        </AnimatePresence>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}
